import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  TrendingUp, 
  Users, 
  MousePointerClick,
  Eye,
  BarChart3,
  Download,
  RefreshCw,
  ExternalLink,
  X
} from 'lucide-react';

const LandingPagePromotions = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [popups, setPopups] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedPopup, setSelectedPopup] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 
    (window.location.origin.includes("localhost") 
      ? "http://localhost:8000" 
      : window.location.origin + "/pandvah-backend");

  useEffect(() => {
    fetchAnalytics();
    fetchPopups();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/popups/analytics/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPopups = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/popups/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPopups(response.data.popups);
      }
    } catch (error) {
      console.error('Error fetching popups:', error);
    }
  };

  const fetchSubmissions = async (popupId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/popups/${popupId}/submissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSubmissions(response.data.submissions);
        setSelectedPopup(popups.find(p => p.id === popupId));
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header with Refresh Button */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Popup Analytics
                  </h2>
                </div>
                
                {/* Refresh Button */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      fetchAnalytics();
                      fetchPopups();
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 transition-colors flex items-center justify-center"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-1">
                  <Eye className="h-6 w-6 text-theme-blue" />
                </div>
                <p className="text-xs text-theme-blue font-medium">Total Popups</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">
                  {analytics?.overview?.total_popups || 0}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between mb-1">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-xs text-green-600 font-medium">Active Popups</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {analytics?.overview?.active_popups || 0}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between mb-1">
                  <MousePointerClick className="h-6 w-6 text-purple-600" />
                </div>
                <p className="text-xs text-purple-600 font-medium">Total Submissions</p>
                <p className="text-2xl font-bold text-purple-900 mt-1">
                  {analytics?.overview?.total_submissions || 0}
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center justify-between mb-1">
                  <BarChart3 className="h-6 w-6 text-orange-600" />
                </div>
                <p className="text-xs text-orange-600 font-medium">Avg Submissions</p>
                <p className="text-2xl font-bold text-orange-900 mt-1">
                  {analytics?.overview?.conversion_rate || 0}
                </p>
                <p className="text-xs text-orange-700 mt-0.5">per popup</p>
              </div>
            </div>

            {/* Top Performing Popups */}
            {analytics?.top_performing_popups && analytics.top_performing_popups.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 mb-6">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-900">Top Performing Popups</h3>
                </div>
                <div className="p-4 max-h-[400px] overflow-y-auto">
                  <div className="space-y-2">
                    {analytics.top_performing_popups.map((popup, index) => (
                      <div key={popup._id} className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg hover:from-blue-100 hover:to-blue-50 transition-colors border border-blue-100">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="bg-theme-blue text-white font-bold rounded-full w-7 h-7 flex items-center justify-center text-xs">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{popup.popup_name || 'Unnamed Popup'}</h4>
                            <p className="text-xs text-gray-600">{popup.count} submissions</p>
                          </div>
                        </div>
                        <button
                          onClick={() => fetchSubmissions(popup._id)}
                          className="text-xs font-medium text-theme-blue hover:text-blue-700 flex items-center px-2 py-1 hover:bg-blue-100 rounded transition-colors"
                        >
                          View
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* All Popups Table */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">All Popups</h3>
              </div>
              
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Popup Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Submissions
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {popups.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-4 py-12 text-center text-sm text-gray-500">
                          No popups created yet
                        </td>
                      </tr>
                    ) : (
                      popups.map((popup) => (
                        <tr key={popup.id} className="hover:bg-blue-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{popup.name}</div>
                            <div className="text-xs text-gray-500">{popup.configuration?.title}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                              popup.enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {popup.enabled ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                            {popup.submission_count || 0}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {new Date(popup.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-xs font-medium">
                            <button
                              onClick={() => fetchSubmissions(popup.id)}
                              className="text-theme-blue hover:text-blue-800 font-semibold hover:underline"
                            >
                              View Submissions
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Submissions Modal */}
        {selectedPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              {/* Sticky Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <div>
                  <h3 className="text-base font-semibold text-white">{selectedPopup.name}</h3>
                  <p className="text-xs text-white text-opacity-90 mt-0.5">
                    {submissions.length} total submission{submissions.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedPopup(null);
                    setSubmissions([]);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {submissions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No submissions yet for this popup
                  </div>
                ) : (
                  <div className="space-y-4">
                    {submissions.map((submission) => (
                      <div key={submission.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="mb-3">
                          <span className="text-sm text-gray-500">
                            {new Date(submission.submitted_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(submission.form_data).map(([key, value]) => (
                            <div key={key}>
                              <label className="text-xs font-medium text-gray-500 uppercase">{key}</label>
                              <p className="text-sm text-gray-900 mt-1">{value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sticky Footer */}
              <div className="px-5 py-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    setSelectedPopup(null);
                    setSubmissions([]);
                  }}
                  className="w-full px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingPagePromotions;
